(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const strings = theme.strings;
  const emit = (name, data) => document.dispatchEvent(new CustomEvent('even:track', { detail: { name, data } }));

  const withDelimiters = (value, decimals, thousands = ',', decimal = '.') => {
    const parts = value.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };

  const formatMoney = (cents) => {
    const amount = (cents || 0) / 100;
    const format = theme.moneyFormat || '${{amount}}';
    const out = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(amount, 0);
        case 'amount_with_comma_separator': return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(amount, 2, "'", '.');
        default: return withDelimiters(amount, 2);
      }
    });
    const el = document.createElement('textarea');
    el.innerHTML = out;
    return el.value;
  };

  const fill = (template, vars) =>
    (template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (key in vars ? vars[key] : ''));

  const setText = (el, text) => {
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  };

  const cart = {
    get el() { return document.querySelector('[data-cart-drawer]'); },
    lastFocus: null,

    open() {
      const el = this.el;
      if (!el) return;
      this.lastFocus = document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('no-scroll');
      el.querySelector('.cart-drawer__panel')?.focus();
    },

    close() {
      const el = this.el;
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('no-scroll');
      this.lastFocus?.focus?.();
    },

    render(sections) {
      const html = sections && sections['cart-drawer'];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]');
      const current = this.el;
      if (!fresh || !current) return;
      current.innerHTML = fresh.innerHTML;
      current.dataset.count = fresh.dataset.count;
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = fresh.dataset.count;
        badge.hidden = fresh.dataset.count === '0';
      });
    },

    async change(payload) {
      const drawer = this.el;
      drawer?.classList.add('is-loading');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...payload, sections: ['cart-drawer'] }),
        });
        const json = await res.json();
        this.render(json.sections);
      } finally {
        this.el?.classList.remove('is-loading');
      }
    },
  };

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-cart-open]');
    if (openTrigger) {
      event.preventDefault();
      cart.open();
      return;
    }
    if (event.target.closest('[data-cart-close]')) {
      cart.close();
      return;
    }
    const line = event.target.closest('[data-line-key]');
    if (!line) return;
    const qtyButton = event.target.closest('[data-line-qty]');
    if (qtyButton) {
      cart.change({ id: line.dataset.lineKey, quantity: Number(qtyButton.dataset.lineQty) });
      return;
    }
    const planButton = event.target.closest('[data-line-plan]');
    if (planButton) {
      cart.change({
        id: line.dataset.lineKey,
        quantity: Number(line.dataset.lineQuantity),
        selling_plan: Number(planButton.dataset.linePlan),
      });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && cart.el?.classList.contains('is-open')) cart.close();
  });

  class ProductSection {
    constructor(root) {
      this.root = root;
      this.product = JSON.parse(root.querySelector('[data-product-json]').textContent);
      this.form = root.querySelector('[data-product-form]');
      this.variantInput = root.querySelector('[data-variant-input]');
      this.planInput = root.querySelector('[data-selling-plan-input]');
      this.planSelect = root.querySelector('[data-plan-select]');
      this.submit = root.querySelector('[data-atc]');
      this.error = root.querySelector('[data-form-error]');
      this.sticky = root.querySelector('[data-sticky-atc]');
      this.days = (root.dataset.days || '').split(',').map((d) => parseInt(d, 10) || 0);
      this.firstExtra = (Number(root.dataset.firstExtra) || 0) / 100;
      this.syncPlan = root.dataset.syncPlan === 'true';
      this.variantId = Number(this.variantInput.value);
      const checked = root.querySelector('[data-purchase-type]:checked');
      this.mode = checked ? checked.value : 'onetime';
      this.bind();
      this.syncPlanToTier();
      this.render();
    }

    bind() {
      this.root.querySelectorAll('[data-tier]').forEach((input) =>
        input.addEventListener('change', () => {
          this.variantId = Number(input.value);
          this.syncPlanToTier();
          this.render();
        }),
      );
      this.root.querySelectorAll('[data-purchase-type]').forEach((input) =>
        input.addEventListener('change', () => {
          this.mode = input.value;
          this.render();
        }),
      );
      this.planSelect?.addEventListener('change', () => {
        const subscribe = this.root.querySelector('[data-purchase-type][value="subscribe"]');
        if (subscribe) subscribe.checked = true;
        this.mode = 'subscribe';
        this.render();
      });
      this.form.addEventListener('submit', (event) => this.addToCart(event));

      this.root.querySelectorAll('[data-thumb]').forEach((thumb) =>
        thumb.addEventListener('click', () => {
          const target = document.getElementById(thumb.dataset.thumb);
          target?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          this.root.querySelectorAll('[data-thumb]').forEach((t) => t.classList.toggle('is-active', t === thumb));
        }),
      );

      // Sticky buy bar: on from the first scroll (the real button sits ~2 screens down on a phone), off only while
      // the real button is on screen. Arrae/Grüns/Remade keep theirs pinned the same way.
      if (this.sticky && this.submit && 'IntersectionObserver' in window) {
        let atcVisible = false;
        const update = () => {
          this.sticky.hidden = atcVisible || window.scrollY < 250;
          document.documentElement.classList.toggle('has-sticky-atc', !this.sticky.hidden);
        };
        new IntersectionObserver(([entry]) => { atcVisible = entry.isIntersecting; update(); }).observe(this.submit);
        window.addEventListener('scroll', update, { passive: true });
      }
    }

    syncPlanToTier() {
      if (!this.syncPlan || !this.planSelect || this.planSelect.tagName !== 'SELECT') return;
      const index = this.product.variants.findIndex((v) => v.id === this.variantId);
      if (index >= 0 && index < this.planSelect.options.length) this.planSelect.selectedIndex = index;
    }

    get variant() {
      return this.product.variants.find((v) => v.id === this.variantId) || this.product.variants[0];
    }

    get planId() {
      return this.planSelect ? Number(this.planSelect.value) : null;
    }

    get plan() {
      for (const group of this.product.selling_plan_groups || []) {
        const plan = group.selling_plans.find((p) => p.id === this.planId);
        if (plan) return plan;
      }
      return null;
    }

    adjust(price, adjustment) {
      if (!adjustment) return price;
      switch (adjustment.value_type) {
        case 'percentage': return Math.round(price * (1 - adjustment.value / 100));
        case 'fixed_amount': return Math.max(0, price - adjustment.value);
        case 'price': return adjustment.value;
        default: return price;
      }
    }

    subscriptionPrices(variant) {
      if (!this.planId) return null;
      const allocation = (variant.selling_plan_allocations || []).find((a) => a.selling_plan_id === this.planId);
      if (!allocation) return null;
      let first = allocation.price;
      let later = null;
      const allocationAdjustments = allocation.price_adjustments || [];
      if (allocationAdjustments.length) {
        first = allocationAdjustments[0].price;
        if (allocationAdjustments[1]) later = allocationAdjustments[1].price;
      } else if (this.plan && this.plan.price_adjustments?.length > 1) {
        later = this.adjust(variant.price, this.plan.price_adjustments[1]);
      }
      if (later === null && this.firstExtra > 0) {
        later = first;
        first = Math.round(first * (1 - this.firstExtra));
      }
      return { first, later, ongoing: later ?? first };
    }

    render() {
      const variant = this.variant;
      const subscribe = this.mode === 'subscribe' && this.planId;
      const sub = this.subscriptionPrices(variant);

      this.variantInput.value = variant.id;
      if (this.planInput) {
        this.planInput.disabled = !subscribe;
        this.planInput.value = subscribe ? this.planId : '';
      }

      this.root.querySelectorAll('[data-tier]').forEach((input) => {
        input.closest('.tier').classList.toggle('is-selected', input.checked);
        const tierVariant = this.product.variants.find((v) => v.id === Number(input.value));
        const index = this.product.variants.indexOf(tierVariant);
        const days = this.days[index];
        const perDayEl = this.root.querySelector(`[data-tier-perday="${input.value}"]`);
        if (!tierVariant || !days || !perDayEl) return;
        const tierSub = subscribe ? this.subscriptionPrices(tierVariant) : null;
        const price = tierSub ? tierSub.ongoing : tierVariant.price;
        setText(perDayEl, fill(strings.perDay, { price: formatMoney(Math.round(price / days)) }));
      });

      this.root.querySelectorAll('[data-purchase-type]').forEach((input) =>
        input.closest('.option').classList.toggle('is-selected', input.checked),
      );

      if (sub) {
        setText(this.root.querySelector('[data-sub-price]'), formatMoney(sub.first));
        setText(this.root.querySelector('[data-sub-compare]'), sub.first < variant.price ? formatMoney(variant.price) : '');
        const pct = Math.round((1 - sub.first / variant.price) * 100);
        setText(this.root.querySelector('[data-sub-save]'), pct > 0 ? fill(strings.savePercent, { percent: pct }) : '');
        setText(
          this.root.querySelector('[data-sub-then]'),
          sub.later !== null && sub.later !== sub.first ? fill(strings.thenEvery, { price: formatMoney(sub.later) }) : '',
        );
      }

      setText(this.root.querySelector('[data-ot-price]'), formatMoney(variant.price));
      setText(
        this.root.querySelector('[data-ot-compare]'),
        variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '',
      );

      const price = subscribe && sub ? sub.first : variant.price;
      const label = !variant.available ? strings.soldOut : subscribe ? strings.startSubscription : strings.addToCart;
      this.submit.disabled = !variant.available;
      setText(this.root.querySelector('[data-atc-label]'), label);
      setText(this.root.querySelector('[data-atc-price]'), variant.available ? formatMoney(price) : '');

      const stickyButton = this.root.querySelector('[data-sticky-button]');
      if (stickyButton) {
        stickyButton.textContent = variant.available ? `${label} · ${formatMoney(price)}` : label;
        stickyButton.disabled = !variant.available;
      }
      setText(this.root.querySelector('[data-sticky-price]'), formatMoney(price));

      if (this.root.dataset.updateUrl !== 'true') return;
      if (!window.location.protocol.startsWith('http')) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      if (subscribe) url.searchParams.set('selling_plan', this.planId);
      else url.searchParams.delete('selling_plan');
      window.history.replaceState({}, '', url);
    }

    async addToCart(event) {
      event.preventDefault();
      this.error.hidden = true;
      this.submit.classList.add('is-loading');
      this.submit.disabled = true;
      const body = new FormData(this.form);
      body.append('sections', 'cart-drawer');
      if (window.location.pathname.startsWith('/')) body.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          emit('even_add_to_cart_error', { message: json.description || json.message || '' });
          setText(this.error, json.description || json.message || (typeof json.errors === 'string' ? json.errors : '') || strings.cartError);
          return;
        }
        cart.render(json.sections);
        cart.open();
        emit('even_add_to_cart', {
          variant_id: this.variantId,
          selling_plan: this.mode === 'subscribe' ? this.planId : null,
          purchase_type: this.mode,
          tier_position: this.product.variants.findIndex((v) => v.id === this.variantId) + 1,
          value: (json.final_line_price ?? json.line_price ?? 0) / 100,
          surface: this.root.dataset.updateUrl === 'true' ? 'product_page' : 'homepage_buy_box',
        });
      } catch (e) {
        setText(this.error, strings.cartError);
      } finally {
        this.submit.classList.remove('is-loading');
        this.submit.disabled = !this.variant.available;
      }
    }
  }

  const store = {
    get(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (e) {} },
  };

  const initEmailPopup = () => {
    const popup = document.querySelector('[data-email-popup]');
    if (!popup || popup.dataset.ready) return;
    popup.dataset.ready = 'true';
    const teaser = document.querySelector('[data-email-teaser]');
    const cooldownDays = Number(popup.dataset.cooldown) || 14;
    const KEY = 'even:popup-until';
    const SUBMITTED = 'even:popup-submitted';
    const panel = popup.querySelector('.email-popup__panel');
    let lastFocus = null;

    const snooze = (days) => store.set(KEY, String(Date.now() + days * 86400000));
    const snoozed = () => Number(store.get(KEY) || 0) > Date.now();

    const open = () => {
      if (!popup.hidden) return;
      lastFocus = document.activeElement;
      popup.hidden = false;
      emit('even_popup_view', {});
      if (teaser) teaser.hidden = true;
      document.documentElement.classList.add('no-scroll');
      panel.focus();
    };
    const close = () => {
      popup.hidden = true;
      emit('even_popup_close', {});
      document.documentElement.classList.remove('no-scroll');
      snooze(cooldownDays);
      if (teaser && store.get(SUBMITTED) !== 'true') teaser.hidden = false;
      lastFocus?.focus?.();
    };

    popup.querySelectorAll('[data-popup-close]').forEach((el) => el.addEventListener('click', close));
    popup.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    teaser?.addEventListener('click', open);

    popup.querySelector('[data-popup-form]')?.addEventListener('submit', () => {
      store.set(SUBMITTED, 'pending');
      emit('even_popup_submit', {});
    });

    const copy = popup.querySelector('[data-copy-code]');
    copy?.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(copy.dataset.copyCode); } catch (e) {}
      const label = copy.querySelector('[data-copy-label]');
      if (label) label.textContent = strings.copied || 'Copied';
    });

    if (popup.querySelector('[data-popup-success]') && store.get(SUBMITTED) === 'pending') {
      store.set(SUBMITTED, 'true');
      snooze(365);
      open();
      return;
    }

    if (store.get(SUBMITTED) === 'true') return;
    if (snoozed()) {
      if (teaser) teaser.hidden = false;
      return;
    }

    // Paid-ad visitors already have the 40% subscriber offer; a 15% popup on top contradicts it (competitor audit 10/12:
    // Grüns/Lemme/Remade show none on paid traffic). Same on the product page, where the buy box is the offer.
    // Those visitors only get the small teaser tab, plus exit intent on desktop.
    const q = new URLSearchParams(location.search);
    try {
      if (q.get('fbclid') || q.get('gclid') || q.get('ttclid') || /paid|cpc|social_ad/.test(q.get('utm_medium') || '')) {
        sessionStorage.setItem('even:paid', '1');
      }
    } catch (e) {}
    let paid = false;
    try { paid = sessionStorage.getItem('even:paid') === '1'; } catch (e) {}
    const quiet = paid || document.body.classList.contains('template-product');

    let fired = false;
    const trigger = () => {
      if (fired || document.querySelector('[data-cart-drawer].is-open')) return;
      fired = true;
      open();
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onExit);
    };
    const threshold = (Number(popup.dataset.scroll) || 40) / 100;
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= threshold) trigger();
    };
    const onExit = (e) => { if (!e.relatedTarget && e.clientY <= 0) trigger(); };
    document.addEventListener('mouseout', onExit);
    if (quiet) {
      // Desktop keeps the side tab; on phones it covered the gallery thumbnails and the buy box.
      if (teaser && window.matchMedia('(min-width: 750px)').matches) teaser.hidden = false;
      return;
    }
    window.setTimeout(trigger, (Number(popup.dataset.delay) || 8) * 1000);
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  const initAnnouncements = () => {
    document.querySelectorAll('[data-announcements]').forEach((track) => {
      const items = [...track.children];
      if (items.length < 2 || track.dataset.ready) return;
      track.dataset.ready = 'true';
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let i = 0;
      let paused = false;
      track.addEventListener('mouseenter', () => { paused = true; });
      track.addEventListener('mouseleave', () => { paused = false; });
      window.setInterval(() => {
        if (paused) return;
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 4500);
    });
  };

  initAnnouncements();
  initEmailPopup();
  document.addEventListener('shopify:section:load', initEmailPopup);

  const init = () => document.querySelectorAll('[data-product-section]').forEach((root) => {
    if (!root.productSection) root.productSection = new ProductSection(root);
  });

  init();
  document.addEventListener('shopify:section:load', init);
})();
